import React, {useContext} from 'react';
import {defineMessages, useIntl} from 'react-intl';
import type {ButtonColumn, DataGridColumn, DataGridServerProps, TextColumn} from '@igp/shared/components/data-grid';
import {DataGridServer} from '@igp/shared/components/data-grid';
import {LocalizedText} from '@igp/shared/components/i18n';
import {ModalContext} from '@igp/shared/components/modal';

import {useReferralLinkConfigActionStatuses, useReferralLinkConfigRemove} from '../hooks';
import type {ReferralLinkConfigDataGridColumn, ReferralLinkConfigViewModel} from '../types';

import {EditReferralLinkConfigModal} from './EditReferralLinkConfigModal';

const localized = defineMessages({
    aal: {
        id: 'ReferralLinkConfigDataGrid_aal',
        defaultMessage: 'AAL',
    },
    license: {
        id: 'ReferralLinkConfigDataGrid_license',
        defaultMessage: 'License',
    },
    referralLinkBaseUrl: {
        id: 'ReferralLinkConfigDataGrid_referralLinkBaseUrl',
        defaultMessage: 'Referral Link Base URL',
    },
    edit: {
        id: 'ReferralLinkConfigDataGrid_edit',
        defaultMessage: 'Edit',
    },
    delete: {
        id: 'ReferralLinkConfigDataGrid_delete',
        defaultMessage: 'Delete',
    },
    cancel: {
        id: 'ReferralLinkConfigDataGrid_cancel',
        defaultMessage: 'Cancel',
    },
    editModalTitle: {
        id: 'ReferralLinkConfigDataGrid_editModalTitle',
        defaultMessage: 'Edit Referral Link Configuration',
    },
    all: {
        id: 'ReferralLinkConfigDataGrid_all',
        defaultMessage: 'All',
    },
});

function useColumns(
    columns: ReferralLinkConfigDataGridColumn[],
    handleRemove: (aalGroupName: string, license?: string) => void,
    inProgressIds: string[]
): DataGridColumn<ReferralLinkConfigViewModel>[] {
    const {formatMessage} = useIntl();
    const {openModal, closeModal} = useContext(ModalContext);

    const aalGroupName: TextColumn<ReferralLinkConfigViewModel> = {
        type: 'text',
        field: 'aal_group_name',
        valueGetter: m => m.aal_group_name,
        title: localized.aal,
        flex: 1,
        sortable: true,
    };

    const referralLinkBaseUrl: TextColumn<ReferralLinkConfigViewModel> = {
        type: 'text',
        field: 'referral_link_base_url',
        valueGetter: m => m.referral_link_base_url ?? '',
        title: localized.referralLinkBaseUrl,
        flex: 3,
    };

    const license: TextColumn<ReferralLinkConfigViewModel> = {
        type: 'text',
        field: 'license',
        valueGetter: m => m.license ?? formatMessage(localized.all),
        title: localized.license,
        flex: 1,
    };

    const edit: ButtonColumn<ReferralLinkConfigViewModel> = {
        type: 'button',
        field: 'edit',
        title: localized.edit,
        valueGetter: m => ({
            title: formatMessage(localized.edit),
            onClick: () => {
                openModal({
                    title: <LocalizedText label={localized.editModalTitle} />,
                    body: (
                        <EditReferralLinkConfigModal
                            initialModel={m}
                            onSave={() => {
                                closeModal();
                            }}
                            onCancel={() => {
                                closeModal();
                            }}
                        />
                    ),
                });
            },
            disabled: inProgressIds.includes(m.aal_group_name),
        }),
        width: 100,
    };

    const deleteAction: ButtonColumn<ReferralLinkConfigViewModel> = {
        type: 'button',
        field: 'delete',
        title: localized.delete,
        valueGetter: m => ({
            title: formatMessage(localized.delete),
            onClick: () => handleRemove(m.aal_group_name, m.license),
            disabled: inProgressIds.includes(m.aal_group_name),
        }),
        width: 100,
    };

    const config: Record<ReferralLinkConfigDataGridColumn, DataGridColumn<ReferralLinkConfigViewModel>> = {
        aal_group_name: aalGroupName,
        referral_link_base_url: referralLinkBaseUrl,
        license,
        edit,
        delete: deleteAction,
    };

    return columns.map(column => config[column]);
}

type ReferralLinkConfigDataGridProps = Omit<
    DataGridServerProps<ReferralLinkConfigViewModel, ReferralLinkConfigDataGridColumn>,
    'columns'
> & {
    columns: ReferralLinkConfigDataGridColumn[];
};

export function ReferralLinkConfigDataGrid(props: ReferralLinkConfigDataGridProps) {
    const {handleRemove} = useReferralLinkConfigRemove();
    const {inProgressIds} = useReferralLinkConfigActionStatuses(props.rows.map(r => r.aal_group_name));

    const columnsConfig = useColumns(props.columns, handleRemove, inProgressIds);

    return <DataGridServer {...props} columns={columnsConfig} />;
}
